import { useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
// material
import { alpha, useTheme, styled } from "@material-ui/core/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { useDispatch, useSelector } from "src/redux/store";
import { togglePin } from "src/redux/slices/pinnedNavSlice";
import { savePinnedNavItems } from "src/Api/new/pinnedNav/pinnedNav-api";

// Tilted location pin icons (map-marker style)
const pinFill = "mdi:map-marker";
const pinOutline = "mdi:map-marker-outline";

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(5),
  color: theme.palette.text.primary,
}));

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  "&:before": {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: "none",
    position: "absolute",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ----------------------------------------------------------------------
// Separate component for child nav items so hooks are called at the top level

NavChildItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  showPin: PropTypes.bool,
};

function NavChildItem({ item, active, showPin }) {
  const dispatch = useDispatch();
  const [hovered, setHovered] = useState(false);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const pinnedItems = useSelector((state) => state.pinnedNav?.pinnedItems ?? []);

  const { title, path } = item;
  const isActiveSub = active(path);
  const isPinned = pinnedItems.some((p) => p.path === path);

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
  };

  const handlePin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(togglePin({ title, path }));
    const newList = isPinned
      ? pinnedItems.filter((p) => p.path !== path)
      : [...pinnedItems, { title, path }];
    await savePinnedNavItems(newList);
  };

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ ...(isActiveSub && activeSubStyle) }}
    >
      <ListItemIconStyle>
        <Box
          component="span"
          sx={{
            width: 4,
            height: 4,
            display: "flex",
            borderRadius: "50%",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "text.disabled",
            transition: (theme) => theme.transitions.create("transform"),
            ...(isActiveSub && {
              transform: "scale(2)",
              bgcolor: "primary.main",
            }),
          }}
        />
      </ListItemIconStyle>

      <ListItemText disableTypography primary={title} />

      {showPin && (
        <Tooltip title={isPinned ? "Unpin" : "Pin to top"} placement="right">
          <IconButton
            size="small"
            onClick={handlePin}
            sx={{
              ml: "auto",
              p: 0.5,
              opacity: hovered || isPinned ? 1 : 0,
              transition: "opacity 0.2s",
              color: isPinned ? "primary.main" : "text.disabled",
              "&:hover": { color: "primary.main" },
            }}
          >
            <Icon icon={isPinned ? pinFill : pinOutline} width={14} height={14} />
          </IconButton>
        </Tooltip>
      )}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

NavItem.propTypes = {
  active: PropTypes.func,
  isShow: PropTypes.bool,
  item: PropTypes.object,
  showPin: PropTypes.bool,
};

function NavItem({ item, active, isShow, showPin = false }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { title, path, icon, info, children } = item;
  const isActiveRoot = active(item.path);
  const [open, setOpen] = useState(isActiveRoot);
  const [hovered, setHovered] = useState(false);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const pinnedItems = useSelector((state) => state.pinnedNav?.pinnedItems ?? []);
  const isPinned = pinnedItems.some((p) => p.path === path);

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    "&:before": { display: "block" },
  };

  const handleOpen = () => setOpen((prev) => !prev);

  const handlePinClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(togglePin({ title, path }));
    const newList = isPinned
      ? pinnedItems.filter((p) => p.path !== path)
      : [...pinnedItems, { title, path }];
    await savePinnedNavItems(newList);
  };

  // Parent item with children (collapsible)
  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{ ...(isActiveRoot && activeRootStyle) }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((child) => (
                <NavChildItem
                  key={child.title}
                  item={child}
                  active={active}
                  showPin={showPin}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  // Leaf item (no children)
  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ ...(isActiveRoot && activeRootStyle) }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}

          {showPin && (
            <Tooltip title={isPinned ? "Unpin" : "Pin to top"} placement="right">
              <IconButton
                size="small"
                onClick={handlePinClick}
                sx={{
                  ml: "auto",
                  p: 0.5,
                  opacity: hovered || isPinned ? 1 : 0,
                  transition: "opacity 0.2s",
                  color: isPinned ? "primary.main" : "text.disabled",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <Icon icon={isPinned ? pinFill : pinOutline} width={15} height={15} />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </ListItemStyle>
  );
}

// ----------------------------------------------------------------------

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array,
  showPin: PropTypes.bool,
};

export default function NavSection({ navConfig, isShow = true, showPin = false, ...other }) {
  const { pathname } = useLocation();
  const match = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item) => (
              <NavItem
                key={item.title}
                item={item}
                active={match}
                isShow={isShow}
                showPin={showPin}
              />
            ))}
          </List>
        );
      })}
    </Box>
  );
}
